// Typed client for /api/feature-requests. Row types come from the shared
// schema; view-model extras (author byline, hasVoted) are declared here.
import axios from "axios";
import { FeatureRequest, FeatureRequestComment } from "../../db/schema";

export type FeatureRequestStatus = FeatureRequest["status"];
export type FeatureRequestCategory = FeatureRequest["category"];
export type Author = { name: string; agency: string | null };
export type FeatureRequestItem = FeatureRequest & { author: Author; hasVoted: boolean; recentVotes: number; isOwn: boolean };
// `removed`: a hidden parent kept as a placeholder because it still has
// visible replies (non-staff view only).
export type FeatureRequestCommentItem = FeatureRequestComment & { author: Author; isOwn: boolean; removed?: boolean };
// `subscribed`: whether the caller gets emails for this thread (author,
// upvoters, and commenters are in by default; unsubscribe is an override).
export type FeatureRequestDetail = FeatureRequestItem & { comments: FeatureRequestCommentItem[]; subscribed: boolean };
export type Roadmap = { planned: FeatureRequestItem[]; in_progress: FeatureRequestItem[]; delivered: FeatureRequestItem[] };
export type Me = { isStaff: boolean; termsVersion: number; acceptedVersion: number | null };
export type Sort = "top" | "new" | "trending";

// Chip classes follow SEVERITY_CLASSES in Investigations/api.ts so the two
// boards read as one system. `dot` is the solid marker for roadmap columns.
export const STATUS_META: Record<FeatureRequestStatus, { label: string; chip: string; dot: string; hint: string }> = {
  open: { label: "Open", chip: "bg-gray-100 text-gray-700 dark:bg-base-200 dark:text-base-content/70", dot: "bg-gray-400", hint: "Gathering votes" },
  under_review: { label: "Under review", chip: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400", dot: "bg-amber-500", hint: "We're looking at it" },
  planned: { label: "Planned", chip: "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300", dot: "bg-blue-500", hint: "On the roadmap" },
  in_progress: { label: "In progress", chip: "bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300", dot: "bg-purple-500", hint: "Being built now" },
  delivered: { label: "Delivered", chip: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400", dot: "bg-green-500", hint: "Shipped" },
  declined: { label: "Not planned", chip: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400", dot: "bg-red-500", hint: "Not something we'll build" },
};
export const STATUS_ORDER: FeatureRequestStatus[] = ["open", "under_review", "planned", "in_progress", "delivered", "declined"];

export const CATEGORY_LABELS: Record<FeatureRequestCategory, string> = {
  reports: "Report writing",
  editor: "Editor",
  forms: "Forms",
  redactions: "Redactions",
  investigations: "Investigations",
  mobile: "Mobile app",
  integrations: "Integrations",
  other: "Other",
};
export const CATEGORY_ORDER: FeatureRequestCategory[] = ["reports", "editor", "forms", "redactions", "investigations", "mobile", "integrations", "other"];

export const SORT_LABELS: Record<Sort, string> = { top: "Top", new: "New", trending: "Trending" };

export const TITLE_MAX = 140;
export const BODY_MAX = 4000;

// The server's 412 when the caller hasn't accepted the current terms.
export function isTermsRequired(error: any): boolean {
  return error?.response?.status === 412 && error?.response?.data?.error === "TERMS_ACCEPTANCE_REQUIRED";
}

export function byline(item: { author: Author }): string {
  return item.author.agency ? `${item.author.name} · ${item.author.agency}` : item.author.name;
}

const base = "/api/feature-requests";

export const api = {
  me: () => axios.get<Me>(`${base}/me`).then((r) => r.data),
  acceptTerms: (version: number) => axios.post<{ acceptedVersion: number }>(`${base}/accept-terms`, { version }).then((r) => r.data),

  list: (params: { status?: string; category?: string; sort?: Sort; q?: string; page?: number }) =>
    axios.get<{ items: FeatureRequestItem[]; hasMore: boolean }>(base, { params }).then((r) => r.data),
  roadmap: () => axios.get<Roadmap>(`${base}/roadmap`).then((r) => r.data),
  changelog: () => axios.get<FeatureRequestItem[]>(`${base}/changelog`).then((r) => r.data),

  create: (body: { title: string; body: string; category: FeatureRequestCategory }) => axios.post<FeatureRequestItem>(base, body).then((r) => r.data),
  get: (id: string) => axios.get<FeatureRequestDetail | { mergedInto: string }>(`${base}/${id}`).then((r) => r.data),
  update: (id: string, body: Partial<{ title: string; body: string; category: FeatureRequestCategory }>) =>
    axios.patch<FeatureRequestItem>(`${base}/${id}`, body).then((r) => r.data),
  remove: (id: string) => axios.delete(`${base}/${id}`),

  vote: (id: string) => axios.post<{ voteCount: number; hasVoted: boolean }>(`${base}/${id}/vote`).then((r) => r.data),
  unvote: (id: string) => axios.delete<{ voteCount: number; hasVoted: boolean }>(`${base}/${id}/vote`).then((r) => r.data),
  // One call for every view: flips the vote and returns the server's count.
  toggleVote: (item: Pick<FeatureRequestItem, "id" | "hasVoted">) => (item.hasVoted ? api.unvote(item.id) : api.vote(item.id)),

  comment: (id: string, body: string, parentId?: string | null) =>
    axios.post<FeatureRequestCommentItem>(`${base}/${id}/comments`, { body, parentId: parentId || undefined }).then((r) => r.data),
  deleteComment: (id: string, commentId: string) => axios.delete(`${base}/${id}/comments/${commentId}`),
  setSubscription: (id: string, subscribed: boolean) => axios.put<{ subscribed: boolean }>(`${base}/${id}/subscription`, { subscribed }).then((r) => r.data),

  // Staff
  setStatus: (id: string, body: { status: FeatureRequestStatus; release_note?: string; staff_response?: string; notify?: boolean }) =>
    axios.patch<FeatureRequestItem>(`${base}/${id}/status`, body).then((r) => r.data),
  notifyCount: (id: string) => axios.get<{ count: number }>(`${base}/${id}/notify-count`).then((r) => r.data.count),
  merge: (id: string, targetId: string) => axios.post<FeatureRequestItem>(`${base}/${id}/merge`, { targetId }).then((r) => r.data),
  hide: (id: string) => axios.post<FeatureRequestItem>(`${base}/${id}/hide`).then((r) => r.data),
  unhide: (id: string) => axios.post<FeatureRequestItem>(`${base}/${id}/unhide`).then((r) => r.data),
};
